/**
 * Invented reference-sign warning banner.
 *
 * Renders below a section's PaperSurface when the post-generation
 * reference-sign check (backend/spec.py `_section_numeral_check`)
 * found a numeral used as a reference sign that the matter's
 * drawing registry doesn't have.
 *
 * The section prompts tell the model to take numerals only from
 * <reference_signs>. Nothing enforced that: the chip validator flags
 * a REGISTRY numeral left unchipped and tolerates every other number,
 * because on this path an unregistered number is normally a quantity
 * ("40 kPa", "at least 2"). So an invented sign read exactly like a
 * real one and pointed at nothing in the figures.
 *
 * Advisory only — does NOT block freeze/export. The paragraphs are
 * already written and a fabricated sign is a two-character edit. The
 * drafter can:
 *   - dismiss with the X button (clears for this session)
 *   - edit the numeral out, or add it to the drawings
 *   - regenerate the section (a fresh check repopulates the entry)
 *   - freeze the section (hidden once frozen)
 */
import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';

export default function UnregisteredNumeralBanner({
  sectionId,
}: {
  sectionId: string;
}): JSX.Element | null {
  const entry = useSpecStore((s) => s.numeralWarnings[sectionId]);
  const dismiss = useSpecStore((s) => s.dismissNumeralWarning);
  const section = useSpecStore(
    (s) => s.sections.find((sec) => sec.id === sectionId) || null,
  );

  if (!entry || entry.numerals.length === 0) return null;
  if (section?.is_frozen) return null;

  const label = (numeral: string): string => {
    const par = (entry.where || {})[numeral];
    return par ? `${numeral} — first used in [${par}]` : numeral;
  };

  return (
    <div
      className="mt-3 rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-zinc-800"
      role="alert"
      data-testid={`spec-numeral-warning-${sectionId}`}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle
          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600"
          aria-hidden
        />
        <div className="flex-1 space-y-1">
          <div className="font-medium text-amber-900">
            {entry.numerals.length === 1
              ? 'Reference sign not in the drawings'
              : 'Reference signs not in the drawings'}
          </div>
          <div className="text-zinc-700">
            {entry.numerals.length === 1
              ? 'This numeral is written as a reference sign but has '
                + 'nothing in the figures to point at. Add it to the '
                + 'drawings, or edit it out.'
              : 'These numerals are written as reference signs but have '
                + 'nothing in the figures to point at. Add them to the '
                + 'drawings, or edit them out.'}
          </div>
          <ul
            className="ml-2 list-disc pl-3 text-zinc-700"
            data-testid={`spec-numeral-warning-list-${sectionId}`}
          >
            {entry.numerals.map((numeral) => (
              <li key={numeral}>{label(numeral)}</li>
            ))}
          </ul>
        </div>
        <button
          type="button"
          className="rounded-sm p-1 text-zinc-400 hover:bg-amber-100 hover:text-zinc-700"
          onClick={() => dismiss(sectionId)}
          aria-label="Dismiss reference sign warning"
          data-testid={`spec-numeral-warning-dismiss-${sectionId}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
